import { Link } from "react-router-dom";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { Td } from "../../features/web3/forex/components/table";
import { formatDateTime } from "../../lib/format";
import { KIND, PURPOSE, TIER } from "./api";
import {
  AccountLink,
  BASE,
  ListView,
  Money,
  Stat,
  UserLink,
  linkCls,
  mono,
  muted,
  orDash,
  useTab,
} from "./parts";

const TABS = [
  { value: "pnl", label: "P&L" },
  { value: "volume", label: "Volume" },
  { value: "stop-outs", label: "Stop outs" },
  { value: "balance-operations", label: "Balance operations" },
];

// Every report reads real money over the last 30 days unless told otherwise;
// the range actually read comes back and is shown above the table.
const BASE_EMPTY = { kind: "REAL", tier: "", purpose: "", username: "", login: "", from: "", to: "" };
const BASE_FIELDS = [
  { name: "kind", label: "Kind", options: KIND.slice(1) },
  { name: "tier", label: "Tier", options: TIER },
  { name: "purpose", label: "Purpose", options: PURPOSE },
  { name: "username", label: "Username (exact)" },
  { name: "login", label: "MT5 login" },
  { name: "from", label: "From (default 30 days ago)", type: "datetime-local" },
  { name: "to", label: "To (default now)", type: "datetime-local" },
];
const SYMBOL = { name: "symbol", label: "Symbol (exact)" };

const GROUP_BY = ["day", "login", "username", "symbol", "tier", "login,symbol", "login,day", "symbol,day", "username,tier"].map(
  (v) => ({ value: v, label: `By ${v.replace(",", " + ")}` }),
);
const KEYS = ["day", "login", "username", "tier", "symbol"];
const keysOf = (f) => KEYS.filter((k) => (f.groupBy || "day").split(",").includes(k));

const Range = ({ d }) => (
  <p className="text-xs text-muted-foreground">
    {formatDateTime(d.from)} → {formatDateTime(d.to)}
  </p>
);

const keyCell = (k, r) => {
  if (k === "login") return <Link to={`${BASE}/accounts?login=${r.login}`} className={linkCls}>{r.login}</Link>;
  if (k === "username") return <UserLink username={r.username} />;
  return r[k];
};

// Only the grouped keys become columns: the rest are null on every row.
function grouped(figures) {
  return {
    columns: (f) => [
      ...keysOf(f).map((k) => ({ label: k })),
      ...figures.map(([, label]) => ({ label, align: "right" })),
    ],
    row: (r, f) => (
      <tr key={JSON.stringify(keysOf(f).map((k) => r[k]))} className="hover:bg-white/[0.02]">
        {keysOf(f).map((k) => (
          <Td key={k} className={mono}>{keyCell(k, r)}</Td>
        ))}
        {figures.map(([name, , money]) => (
          <Td key={name} align="right" className={money ? "" : mono}>
            {money ? <Money value={r[name]} /> : r[name]}
          </Td>
        ))}
      </tr>
    ),
    head: (figs) => (d) => (
      <div className="space-y-2">
        <Range d={d} />
        <div className="flex flex-wrap gap-3">
          {figs.map(([name, label, money]) => (
            <Stat key={name} label={`Total ${label}`}>
              {money ? <Money value={d.totals[name]} /> : d.totals[name]}
            </Stat>
          ))}
        </div>
      </div>
    ),
  };
}

const PNL = [
  ["realizedProfit", "Realized", true],
  ["commission", "Commission", true],
  ["swap", "Swap", true],
];
// Lots opened counts a round trip once, turnover twice: never call either "volume".
const VOLUME = [
  ["lotsOpened", "Lots", false],
  ["turnover", "Turnover (lots)", false],
  ["commission", "Commission", true],
];

const OWNER_COLUMNS = [{ label: "Login" }, { label: "Username" }, { label: "Kind" }];
const OwnerCells = ({ r }) => (
  <>
    <Td className={mono}><AccountLink id={r.accountId}>{r.login}</AccountLink></Td>
    <Td className={mono}><UserLink username={r.username} /></Td>
    <Td className="text-xs">{r.kind}</Td>
  </>
);

const STOP_OUT_COLUMNS = [
  { label: "Time" },
  { label: "Deal" },
  ...OWNER_COLUMNS,
  { label: "Symbol" },
  { label: "Closing side" },
  { label: "Lots", align: "right" },
  { label: "Price", align: "right" },
  { label: "Profit", align: "right" },
  { label: "Margin level", align: "right" },
  { label: "Equity", align: "right" },
  { label: "Margin", align: "right" },
  { label: "Position" },
  { label: "Server comment" },
];

// One row per closed deal: one stop out event that closed three positions is three rows.
const stopOutRow = (r) => (
  <tr key={r.deal} className="hover:bg-white/[0.02]">
    <Td className={muted}>{formatDateTime(r.time)}</Td>
    <Td className={mono}>{r.deal}</Td>
    <OwnerCells r={r} />
    <Td className={mono}>{r.symbol}</Td>
    <Td className="text-xs">{r.action}</Td>
    <Td align="right" className={mono}>{r.lots}</Td>
    <Td align="right" className={mono}>{r.price}</Td>
    <Td align="right"><Money value={r.profit} /></Td>
    <Td align="right" className={mono}>{r.marginLevel == null ? orDash(null) : `${r.marginLevel}%`}</Td>
    <Td align="right" className={mono}>{orDash(r.equity)}</Td>
    <Td align="right" className={mono}>{orDash(r.margin)}</Td>
    <Td className={mono}>{r.position}</Td>
    <Td className="text-xs">{r.comment}</Td>
  </tr>
);

const OP_TYPES = ["DEPOSIT", "WITHDRAWAL", "TRANSFER", "CHARGE", "ACCESS_FEE", "CREDIT", "OTHER"].map((v) => ({ value: v, label: v }));

const OP_COLUMNS = [
  { label: "Time" },
  { label: "Deal" },
  ...OWNER_COLUMNS,
  { label: "Type" },
  { label: "Amount", align: "right" },
  { label: "Comment" },
  { label: "Record" },
];

const opRow = (r) => (
  <tr key={r.deal} className="hover:bg-white/[0.02]">
    <Td className={muted}>{formatDateTime(r.time)}</Td>
    <Td className={mono}>{r.deal}</Td>
    <OwnerCells r={r} />
    <Td className="text-xs">{r.type}</Td>
    <Td align="right"><Money value={r.amount} /></Td>
    {/* OTHER holds test deals and comments the rules do not know yet. */}
    <Td className="text-xs">{r.comment}</Td>
    <Td className="text-xs">
      {r.transferId ? (
        <Link to={`${BASE}/transfers?open=${r.transferId}`} className={linkCls}>Transfer →</Link>
      ) : r.chargeId ? (
        <Link to={`${BASE}/settlements?tab=charges&open=${r.chargeId}`} className={linkCls}>Charge →</Link>
      ) : (
        orDash(null)
      )}
    </Td>
  </tr>
);

function Tab({ tab }) {
  switch (tab) {
    case "volume": {
      const g = grouped(VOLUME);
      return (
        <ListView path="/reports/admin/volume" empty={{ ...BASE_EMPTY, symbol: "", groupBy: "day" }}
          fields={[{ name: "groupBy", label: "Group", options: GROUP_BY }, ...BASE_FIELDS, SYMBOL]}
          columns={g.columns} row={g.row} head={g.head(VOLUME)} label="groups" />
      );
    }
    case "stop-outs":
      return (
        <ListView path="/reports/admin/stop-outs" empty={{ ...BASE_EMPTY, symbol: "" }}
          fields={[...BASE_FIELDS, SYMBOL]} columns={STOP_OUT_COLUMNS} row={stopOutRow}
          head={(d) => <Range d={d} />} label="deals" />
      );
    case "balance-operations":
      return (
        <ListView path="/reports/admin/balance-operations" empty={{ ...BASE_EMPTY, type: "" }}
          fields={[{ name: "type", label: "Type", options: [{ value: "", label: "All types" }, ...OP_TYPES] }, ...BASE_FIELDS]}
          columns={OP_COLUMNS} row={opRow} head={(d) => <Range d={d} />} label="operations" />
      );
    default: {
      const g = grouped(PNL);
      return (
        <ListView path="/reports/admin/pnl" empty={{ ...BASE_EMPTY, symbol: "", groupBy: "day" }}
          fields={[{ name: "groupBy", label: "Group", options: GROUP_BY }, ...BASE_FIELDS, SYMBOL]}
          columns={g.columns} row={g.row} head={g.head(PNL)} label="groups" />
      );
    }
  }
}

export default function Reports() {
  const [tab, setTab] = useTab("pnl");
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;

  return (
    <QueueLayout
      eyebrow="Forex Admin"
      title="Book"
      accent="reports"
      description="Across every account we own. Real money over the last 30 days unless you pick otherwise; at most 366 days."
      statusOptions={TABS}
      status={tab}
      onStatus={setTab}
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      <Tab key={tab} tab={tab} />
    </QueueLayout>
  );
}
